import React from 'react';
import styled from 'styled-components';
import { deleteBlueshyftCode } from '../../../../api/deposit';
import LoadingButton from '../../../../components/LoadingButton';
import useAsyncAction from '../../../../hooks/useAsyncAction';
import formatCurrency from '../../../../utils/formatCurrency';
import { BlueshyftCode } from './BlueshyftCode';
import { PendingBlueshyftCodes } from './PendingBlueshyftCodes';
import blueshyftLogoPng from './blueshyft-logo-1024x274.png';
import { PaymentPages } from '../DepositService';
import appConfig from '../../../../appConfig';
import InfoBlock from '../../../../components/InfoBlock';

const blueshyftURL = appConfig.portal.blueshyftQrUrl;

export const BlueshyftCodePage = (props: Props) => {
	const { code, pendingBlueshyftCodes, setPreviousCodes, setCurrentPageIndex, previousCodes } = { ...props };
	const [loading, errorMessage, performAction] = useAsyncAction();
	const [selectedCode, setSelectedCode] = React.useState<PendingBlueshyftCodes>({ ...code });
	const [cancelMode, setCancelMode] = React.useState(false);

	React.useEffect(() => {
		performAction(async () => {
			const newPreviousCodes = await pendingBlueshyftCodes();
			setPreviousCodes(newPreviousCodes);
		});
	}, []);

	const CancelUserCode = async () => {
		const selectedCodeId = selectedCode?.id ?? previousCodes?.find((x) => x.code === code?.code)?.id;
		if (selectedCodeId) {
			performAction(async () => {
				if (selectedCode?.id) {
					await deleteBlueshyftCode(selectedCode?.id);
					const previousCodes = await pendingBlueshyftCodes();
					setPreviousCodes(previousCodes);
					setSelectedCode(null as any); // hate this change to react typescript where objects types cant be null.
					setCancelMode(false);
				}
			});
		}
	};

	const PaymentCode: React.FC<PendingBlueshyftCodes> = (thePendingCode: PendingBlueshyftCodes) => {
		const buttonContent = () => {
			if (selectedCode?.code === thePendingCode.code) {
				return (
					<LoadingButton
						className="_w-40 _m-auto _btn _btn-primary-action _rounded"
						onClick={() => setCancelMode(true)}
					>
						CANCEL CODE
					</LoadingButton>
				);
			} else {
				return (
					<LoadingButton
						className="_w-40 _m-auto _btn _btn-primary-action _rounded"
						onClick={() => {
							setSelectedCode(thePendingCode);
						}}
					>
						VIEW CODE
					</LoadingButton>
				);
			}
		};
		return (
			<PendingCode>
				<PendingAmount>
					<Amount>{formatCurrency(thePendingCode.amount)}</Amount>
					<span>Pending In-Cash</span>
				</PendingAmount>
				{buttonContent()}
			</PendingCode>
		);
	};

	const ScanBody = () => {
		return (
			<ScanBodyWrapper>
				<div>
					<strong>Present this QR code to your blueshyft outlet.</strong>
				</div>
				<img src={`${blueshyftURL}${selectedCode.code}/qr`} alt="qrCode" width={157} />
				<div>
					<strong>{selectedCode?.code}</strong>
				</div>
				<div>Your cashup code has been created. Please close this window to keep betting</div>
				<a href={selectedCode?.qrCodeURL} target="_blank" rel="noreferrer" style={{ marginBottom: 12 }}>
					<LoadingButton className="_w-full _m-auto _btn _btn-primary _rounded">PRINT CODE</LoadingButton>
				</a>
				<InfoBlock text="Present your unique code to the outlet and your account balance will automatically update once payment has been confirmed" />
			</ScanBodyWrapper>
		);
	};

	const CancelPage = () => {
		return (
			<CancelPageWrapper>
				<div>
					<strong>Are you sure you would like to cancel this CashUp code?</strong>
				</div>
				<LoadingButton
					onClick={() => CancelUserCode()}
					loading={loading}
					className="_w-full _m-auto _btn _btn-primary _rounded"
				>
					YES
				</LoadingButton>
				<LoadingButton
					onClick={() => setCancelMode(false)}
					className="_w-full _m-auto _btn _btn-primary-action _rounded"
				>
					NO
				</LoadingButton>
			</CancelPageWrapper>
		);
	};

	const HowToPage = () => {
		return (
			<HowToPageWrapper>
				<div>
					<img src={blueshyftLogoPng} alt="blueshyft logo" height={35} />
					<span>Make an instant cash deposit in person at 1,500 blueshyft outlets across Australia</span>
				</div>
				<span>
					<strong>How it works</strong>
				</span>
				<span>
					Enter the amount you wish to deposit and a QR code will be generated. Present this to the blueshyft outlet
					attendant along with your cash for an instant deposit into your account.
				</span>
				<br />
				<span>
					<a onClick={() => setCurrentPageIndex(PaymentPages.Map)}>
						<FindLink>Find blueshyft outlet</FindLink>
					</a>
				</span>
			</HowToPageWrapper>
		);
	};

	const BodyRender = () => {
		if (cancelMode && selectedCode?.code) {
			return <CancelPage />;
		} else if (selectedCode?.code) {
			return <ScanBody />;
		} else return <HowToPage />;
	};

	const PaymentHeaderComponent = () => {
		return (
			<PaymentHeader>
				{previousCodes && previousCodes?.length > 0 ? (
					[...previousCodes]?.reverse().map((code) => {
						return <PaymentCode {...code} key={code.code as string} />;
					})
				) : (
					<div>No Pending In-Cash</div>
				)}
			</PaymentHeader>
		);
	};

	return (
		<>
			<PaymentHeaderComponent />
			<BlueshyftBodyWrapper>{BodyRender()}</BlueshyftBodyWrapper>
		</>
	);
};

interface Props {
	code?: BlueshyftCode;
	pendingBlueshyftCodes: () => Promise<PendingBlueshyftCodes[]>;
	setPreviousCodes: (codes: PendingBlueshyftCodes[]) => void;
	previousCodes: PendingBlueshyftCodes[] | undefined;
	setCurrentPageIndex: (page: PaymentPages) => void;
}

const PendingAmount = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	width: 170px;
	margin-right: 55px;
	margin-left: 8px;
	height: 40px;
	> span {
		color: #000;
		font-size: 13px;
		text-align: center;
		margin: auto;
	}
`;

const Amount = styled.div`
	-webkit-transform: skewX(-10deg);
	-moz-transform: skewX(-10deg);
	-ms-transform: skewX(-10deg);
	transform: skewX(-10deg);
	background-color: #01ca6e;
	min-width: 135px;
	width: fit-content + 10px;
	height: 40px;
	color: white;
	font-size: 34px;
	text-align: center;
	font-weight: bold;
	margin-right: 2px;
`;

const PendingCode = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	width: 100%;
	height: 80px;
	margin-bottom: 20px;
	margin-top: 20px;
`;

const PaymentHeader = styled.div`
	height: 80px;
	border-bottom: 1px solid #e6e6e6;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	overflow-y: scroll;
`;

const BlueshyftBodyWrapper = styled.div`
	display: flex;
	padding-top: 10px;
`;

const ScanBodyWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	font-size: 15px;
	color: #000;
	text-align: center;
	> div {
		margin-bottom: 25px;
	}
	> img {
		margin: auto;
		margin-bottom: 25px;
	}
`;

const CancelPageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	font-size: 15px;
	color: #000;
	text-align: center;
	padding-top: 10px;
	padding-bottom: 20px;
	> div {
		margin-bottom: 27px;
	}
	> button {
		margin-bottom: 11px;
	}
`;

const HowToPageWrapper = styled.div`
	display: flex;
	color: #000;
	font-size: 13px;
	text-align: left;
	padding-top: 10px;
	justify-content: space-between;
	flex-direction: column;
	padding-bottom: 20px;
	> div {
		display: flex;
		flex-direction: row;
		justify-content: left;
		margin-bottom: 10px;
	}
`;

const FindLink = styled.strong`
	cursor: pointer;
	color: #0000ee;
`;
